/**
 * <CorrectionFeedback> — « La correction t'a aidé ? », sous une correction de Sam (molecule).
 *
 * La ligne de l'ancienne fiche (`ActivityDetailsModal.js`) : la question, un pouce levé, un pouce
 * baissé. Le pouce choisi prend la tinte de ce qu'il dit (progression, erreur) ; l'autre reste un
 * glyphe. Contrôlé : l'avis donné et son envoi arrivent en props.
 *
 * La même ligne sert à la fin d'un checkpoint de Sam, avec ses mots (`texts`) : « C'était clair ? »,
 * et les deux pouces disent « C'était clair » / « Ce n'était pas clair ». À renommer en avis
 * générique le jour où un troisième lecteur arrive.
 */
import React from "react"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { type UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"

// L'avis sur la correction : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UICorrectionFeedback }

export type CorrectionFeedbackProps = {
    /** L'avis donné ; `null` tant que l'élève n'a rien dit. */
    value: UICorrectionFeedback | null
    onChange: (value: UICorrectionFeedback) => void
    /** L'avis part : les pouces ne répondent plus. */
    submitting?: boolean
    /** La question et ce que disent les deux pouces. Défaut : ceux de la correction. */
    texts?: CorrectionFeedbackTexts
    /** L'avis n'a pas pu partir : ce qu'on en dit, sous les pouces. Absent : rien. */
    error?: string | null
    testID?: string
}

export type CorrectionFeedbackTexts = { question: string; yes: string; no: string }

const TEXTS: CorrectionFeedbackTexts = {
    question: "La correction t'a aidé ?",
    yes: "Oui, elle m'a aidé",
    no: "Non, elle ne m'a pas aidé",
}

export function CorrectionFeedback(props: CorrectionFeedbackProps) {
    const { value, onChange, submitting = false, texts = TEXTS, error, testID } = props
    const positive = value === "positive"
    const negative = value === "negative"

    const row = (
        <Row gap="2" align="center" justify="center" testID={error != null && error !== "" ? undefined : testID}>
            <Text size="text-sm" color="text-muted">
                {texts.question}
            </Text>
            <IconButton
                iconName="thumbs-up"
                size={44}
                iconSize={20}
                appearance={positive ? "filled" : "transparent"}
                color="successLight"
                iconColor={positive ? "success-strong" : "text-muted"}
                onPress={() => onChange("positive")}
                disabled={submitting}
                accessibilityLabel={texts.yes}
            />
            <IconButton
                iconName="thumbs-down"
                size={44}
                iconSize={20}
                appearance={negative ? "filled" : "transparent"}
                color="dangerLight"
                iconColor={negative ? "danger-strong" : "text-muted"}
                onPress={() => onChange("negative")}
                disabled={submitting}
                accessibilityLabel={texts.no}
            />
        </Row>
    )
    if (error == null || error === "") return row

    return (
        <Stack gap="1" align="center" testID={testID}>
            {row}
            <Text size="text-sm" color="text-danger" center accessibilityRole="alert">
                {error}
            </Text>
        </Stack>
    )
}
